import { useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import { AnalysisProviderDetailsDto } from "@/components/DetailsSection/types";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import ConveyIntegrationContent from "@/components/PageHeader/actions/dialogs/integrations/ConveyIntegrationContent";
import { ALERT_STATUS } from "@/context/alert/types";
import {
  CONVEY_INTEGRATION_FIELD_NAMES,
  ConveyWebErrors,
  ConveyWebObjectState
} from "@/Pages/Integrations/types";
import { useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import { refreshTableAndUpdateDetails } from "@/shared/functions/functions";
import useResponse from "@/shared/hooks/useResponse";
import { useAnalysisProvidersTableStore } from "@/stores/useTableStore";

interface EditConveyIntegrationDialogProps {
  rowData: AnalysisProviderDetailsDto;
  isOpen: boolean;
  onClose: () => void;
}

const EditConveyIntegrationDialog = ({
  rowData,
  isOpen,
  onClose
}: EditConveyIntegrationDialogProps) => {
  const { toMd } = useMediaQueries();
  const { t } = useTranslation();
  const { putData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse, handleCommonError } = useResponse();

  const params = useAnalysisProvidersTableStore(state => state.params);
  const { contains: searchParams, selectedTableItem } = params;

  const { company, name, username, enabled, includeSubCompanies } = rowData;

  const initialIntegrationObject: ConveyWebObjectState = {
    [CONVEY_INTEGRATION_FIELD_NAMES.SelectedCompany]: company || null,
    [CONVEY_INTEGRATION_FIELD_NAMES.Name]: name,
    [CONVEY_INTEGRATION_FIELD_NAMES.Username]: username,
    [CONVEY_INTEGRATION_FIELD_NAMES.Password]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.Enabled]: enabled,
    [CONVEY_INTEGRATION_FIELD_NAMES.IncludeSubCompanies]: includeSubCompanies
  };

  const [conveyWebObject, setConveyWebObject] = useState<ConveyWebObjectState>(
    initialIntegrationObject
  );

  const initialErrors: ConveyWebErrors = {
    [CONVEY_INTEGRATION_FIELD_NAMES.Name]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.Username]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.Password]: ""
  };

  const [errors, setErrors] = useState<ConveyWebErrors>(initialErrors);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);

  const hasErrors = Object.values(errors).some(error => error !== "");

  const optionalFields = new Set([
    CONVEY_INTEGRATION_FIELD_NAMES.Password,
    CONVEY_INTEGRATION_FIELD_NAMES.Enabled
  ]);

  const requiredFieldsFilled = Object.entries(conveyWebObject).every(
    ([key, value]) => {
      if (optionalFields.has(key as CONVEY_INTEGRATION_FIELD_NAMES))
        return true;
      return (
        value !== null && value !== undefined && String(value).trim() !== ""
      );
    }
  );

  const successMessage = t("Alert##edit integration success");

  const resetAllValues = () => {
    setConveyWebObject(initialIntegrationObject);
    setErrors(initialErrors);
    setHasInteracted(false);
  };

  const handleEditSubmit = async () => {
    setIsLoading(true);
    const editableItemHasOpenedDetails = selectedTableItem === rowData.id;

    try {
      const { selectedCompany, ...cleanedConveyWebObject } = conveyWebObject;

      const payloadObject = {
        ...cleanedConveyWebObject,
        id: rowData.id,
        companyId: conveyWebObject.selectedCompany?.id
      };

      const { data } = await putData(
        ApiResources.IntegrationsAnalysisProviders,
        payloadObject
      );

      const errorCodeExists = data?.connectionErrorCode
        ? undefined
        : successMessage;

      refreshTableAndUpdateDetails({
        valueInSearchField: searchParams,
        resourceId: data.id,
        successMessage: errorCodeExists,
        itemHasOpenedDetails: editableItemHasOpenedDetails,
        getTableItemDetails,
        handleResponse
      });

      handleResponse(ALERT_STATUS.Success, successMessage);
      onClose();
      resetAllValues();
    } catch (error) {
      handleCommonError(error, t);
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    !isLoading && onClose();
  };

  const isReadyToConfirm = !hasErrors && requiredFieldsFilled && hasInteracted;

  return (
    <Dialog
      data-testid="edit-convey-integration-dialog"
      title={t("Dialog##convey integration##edit##title")}
      description={t("Dialog##convey integration##edit##description")}
      isTitleSeparator
      isActionsSeparator
      extendToMaxWidth={toMd}
      open={isOpen}
      close={handleClose}
      submit={handleEditSubmit}
      width={toMd ? undefined : DialogWidth.Dialog920px}
      customPaperMargin="48px"
      actions={
        <DialogActions
          onClose={handleClose}
          isLoading={isLoading}
          disabledClose={isLoading}
          isreadyToConfirm={isReadyToConfirm}
          confirmationButtonText={t("Button##save")}
          confirmationButtonTestId="edit-save-convey-integration-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="edit-close-convey-integration-button"
        />
      }
    >
      <ConveyIntegrationContent
        optionalPassword
        isLoading={isLoading}
        conveyWebObject={conveyWebObject}
        errors={errors}
        setErrors={setErrors}
        setConveyWebObject={setConveyWebObject}
        hasInteracted={hasInteracted}
        setHasInteracted={setHasInteracted}
      />
    </Dialog>
  );
};

export default EditConveyIntegrationDialog;
